import { useState, useRef } from 'react';
import ArtZipButton from './ArtZipButton';
import BatchCompletion from './BatchCompletion';
import SourceSheetLink from './SourceSheetLink';

const BACKBOOK_SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1W0S0O2ImjUY5BMLGbNGkeHIH2L9_1i5KYUyaKGCEYyA/edit?gid=833863373#gid=833863373';

function localBatchDate() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Backbook "new stands": the dealer's pre-produced cards order exists, but no
 * stands order was ever created. Capital One's sheet supplies the new stands
 * data (one row per QR code) plus, in its shipping block, the existing cards
 * order each pairs with. Importing creates the stands orders through
 * orders.re3gifts.com (codes + art included), then updates the cards orders
 * like modify & ship. Only the stands batch has downloads — its CSV's CARD
 * columns tell production which shelf cards to kit.
 */
export default function BackbookNewStandsModal({ onClose, onComplete, secretKey, onNeedAuth, onAuthFailed }) {
  const [step, setStep] = useState('select'); // select → preview → imported
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmImport, setConfirmImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();
  const batchDate = localBatchDate();

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/backbook/new-stands/preview', { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to parse file');
      setPreview(data);
      setStep('preview');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('batchDate', batchDate);
      const res = await fetch('/api/backbook/new-stands/generate', { method: 'POST', body: formData });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to generate CSV');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BBNS${batchDate}-upload.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!secretKey) {
      onNeedAuth();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('batchDate', batchDate);
      formData.append('secret', secretKey);
      const res = await fetch('/api/backbook/new-stands/import', { method: 'POST', body: formData });
      const data = await res.json();
      if (res.status === 403) {
        onAuthFailed();
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Import failed');
      setImportResult(data);
      setStep('imported');
      if (onComplete) onComplete();
    } catch (err) {
      setError(err.message);
      setConfirmImport(false);
    } finally {
      setLoading(false);
    }
  };

  const standsBatch = importResult?.batches?.find((b) => b.group === 'stands');

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Backbook — New Stands</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {step === 'select' && "Upload Capital One's new-stands sheet (CSV)"}
              {step === 'preview' && 'Review new stands orders and their pre-produced cards pairs'}
              {step === 'imported' && 'Stands orders created and cards orders updated'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {step === 'select' && (
            <div className="flex flex-col items-center py-6">
              <div className="w-full max-w-md">
                <SourceSheetLink href={BACKBOOK_SHEET_URL} tone="sky" label="to see the latest batches." />
              </div>
              <div
                onClick={() => fileRef.current?.click()}
                className="w-full max-w-md border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-sky-400 hover:bg-sky-50/30 transition-colors"
              >
                <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                {file ? (
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-600">Click to select the new-stands CSV</p>
                    <p className="text-xs text-gray-400 mt-1">New stands data + the cards orders they pair with</p>
                  </>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".csv" onChange={handleFileSelect} className="hidden" />
            </div>
          )}

          {step === 'preview' && preview && (
            <>
              {/* Planned batches */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                <div className="rounded-lg px-4 py-3 border bg-sky-50 border-sky-200">
                  <p className="text-xs text-sky-700 font-medium">Stands (new orders — CSV + art zip after import)</p>
                  <p className="text-sm font-bold font-mono text-gray-900 mt-1">BBNS{batchDate}_stands</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {preview.totals.valid} order{preview.totals.valid !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="rounded-lg px-4 py-3 border bg-sky-50 border-sky-200">
                  <p className="text-xs text-sky-700 font-medium">Cards (pre-produced — no downloads)</p>
                  <p className="text-sm font-bold font-mono text-gray-900 mt-1">BBNS{batchDate}_cards</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {preview.totals.valid} order{preview.totals.valid !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {preview.totals.withIssues > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-4 text-sm text-red-800">
                  <span className="font-semibold">Review required:</span> {preview.totals.withIssues} order
                  {preview.totals.withIssues !== 1 ? 's have' : ' has'} issues. Fix the sheet and re-upload —
                  new-stands imports run all-or-nothing.
                </div>
              )}

              {preview.totals.withWarnings > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 mb-4 text-sm text-amber-800">
                  <span className="font-semibold">Heads up:</span> {preview.totals.withWarnings} order
                  {preview.totals.withWarnings !== 1 ? 's reuse' : ' reuses'} QR ids that already exist in the
                  codes table. This doesn't block the import — check the sheet if it looks unexpected.
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[38vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="border-b border-gray-200">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Dealer</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Name</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">New Stands Order</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Cards Order</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Qty</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Ship To</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Check</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {preview.orders.map((o) => (
                        <tr
                          key={o.orderNumber}
                          className={!o.valid ? 'bg-red-50' : o.warnings?.length ? 'bg-amber-50' : ''}
                        >
                          <td className="px-4 py-2 font-mono text-xs font-semibold text-gray-900">{o.dealerId}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{o.dealerName || o.company || '—'}</td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-600">{o.orderNumber}</td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-600">
                            {o.cardsOrder?.uid || '—'}
                            {o.cardsOrder?.matchedBy === 'dealer' && (
                              <span
                                className="ml-1 font-sans text-[10px] text-gray-400"
                                title="The sheet didn't name a cards order — matched by dealer id (codes_completed, cards-only, 20/40 cards, unbatched)"
                              >
                                via dealer
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-xs tabular-nums">
                            {o.cardsOrder?.qty != null ? (
                              <span className={o.cardsOrder.qty === 20 || o.cardsOrder.qty === 40 ? 'text-gray-600' : 'text-red-600 font-semibold'}>
                                {o.cardsOrder.qty}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-500">{o.shipTo || '—'}</td>
                          <td className="px-4 py-2 text-xs">
                            {o.valid && (
                              <span className="text-emerald-600 font-medium mr-1">Ready</span>
                            )}
                            {o.issues.map((issue, i) => (
                              <span key={`i${i}`} className="inline-block mr-1 mb-0.5 text-[10px] font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                                {issue}
                              </span>
                            ))}
                            {(o.warnings || []).map((warning, i) => (
                              <span key={`w${i}`} className="inline-block mr-1 mb-0.5 text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                {warning}
                              </span>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                {preview.totals.orders} new stands order{preview.totals.orders !== 1 ? 's' : ''} · {preview.totals.codes} QR codes ·
                each pairs with a pre-produced cards order (validated: dealer match, codes_completed, 20 or 40 cards).
                Importing creates the stands orders + art; the cards orders then get the ship-to address,
                status → production, type → full_kit, and the <span className="font-mono">_cards</span> label.
              </p>
            </>
          )}

          {step === 'imported' && importResult && (
            <div className="flex flex-col items-center justify-center py-6">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {importResult.imported.orders} Stands Order{importResult.imported.orders !== 1 ? 's' : ''} Created
              </h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 mb-4 text-xs text-emerald-800 space-y-1">
                <p>✓ Import tool created <span className="font-semibold">{importResult.imported.orders}</span> orders / <span className="font-semibold">{importResult.imported.products}</span> products, art generation kicked off</p>
                <p>✓ {importResult.cardsUpdated} cards order{importResult.cardsUpdated !== 1 ? 's' : ''} updated: address set, status → <span className="font-mono font-semibold">production</span>, type → <span className="font-mono font-semibold">full_kit</span></p>
                <p>✓ Batches: <span className="font-mono font-semibold">{importResult.batches.map((b) => b.label).join(', ')}</span></p>
              </div>

              {importResult.importErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 w-full max-w-lg">
                  <p className="text-xs font-semibold text-red-800 mb-1">Import tool reported errors:</p>
                  <ul className="text-xs text-red-700 font-mono space-y-0.5 max-h-32 overflow-y-auto">
                    {importResult.importErrors.map((e, i) => (
                      <li key={i}>{String(e)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {importResult.cardsIssues.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 w-full max-w-lg">
                  <p className="text-xs font-semibold text-red-800 mb-1">
                    Cards orders that could not be updated (the stands import already happened — resolve these before emailing):
                  </p>
                  <ul className="text-xs text-red-700 space-y-0.5 max-h-32 overflow-y-auto">
                    {importResult.cardsIssues.map((e, i) => (
                      <li key={i}>{String(e)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {standsBatch && (
                <div className="w-full max-w-md border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-mono font-semibold text-gray-900">{standsBatch.label}</p>
                    <p className="text-xs text-gray-400">{standsBatch.orders} stands order{standsBatch.orders !== 1 ? 's' : ''} · print + kit</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <a
                      href={`/api/greenlight/export/${encodeURIComponent(standsBatch.label)}`}
                      download
                      className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                      CSV
                    </a>
                    <ArtZipButton
                      label={standsBatch.label}
                      className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                    >
                      Art ZIP
                    </ArtZipButton>
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400 mb-4 max-w-md text-center">
                The cards batch has no downloads — those cards are already produced. The stands CSV's CARD
                columns tell production which cards to pull for each kit. Art generation takes a minute or
                two — if the art check comes up short, give it a moment and re-check before emailing.
              </p>

              <BatchCompletion
                batches={importResult.batches.map((b) => ({ label: b.label, orders: b.orders }))}
                batchDate={batchDate}
                secretKey={secretKey}
                onNeedAuth={onNeedAuth}
                onAuthFailed={onAuthFailed}
                onSent={onComplete}
              />
            </div>
          )}
        </div>

        {/* Import confirmation */}
        {step === 'preview' && confirmImport && (
          <div className="mx-6 mt-2 bg-emerald-50 border border-emerald-300 rounded-xl px-5 py-4 min-h-0 overflow-y-auto max-h-[35vh]">
            <p className="text-sm font-semibold text-emerald-900 mb-1.5">
              Create {preview.totals.orders} stands order{preview.totals.orders !== 1 ? 's' : ''} and update {preview.totals.orders} cards order{preview.totals.orders !== 1 ? 's' : ''}?
            </p>
            <p className="text-xs text-emerald-800">
              Sends the new stands orders to the import tool (creates orders + QR codes, kicks off art,
              batch <span className="font-mono">BBNS{batchDate}_stands</span>), then updates each paired cards
              order: ship-to address, status → <span className="font-mono font-semibold">production</span>,
              type → <span className="font-mono font-semibold">full_kit</span>, send_authorized →{' '}
              <span className="font-mono font-semibold">TRUE</span>, batch{' '}
              <span className="font-mono">BBNS{batchDate}_cards</span>. The import cannot be un-done from this app.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 flex-shrink-0">
          {step === 'select' && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
                Cancel
              </button>
              <button
                onClick={handlePreview}
                disabled={!file || loading}
                className="px-5 py-2 bg-sky-700 text-white text-sm font-medium rounded-lg hover:bg-sky-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Checking…' : 'Check File'}
              </button>
            </>
          )}
          {step === 'preview' && confirmImport && (
            <>
              <button
                onClick={() => setConfirmImport(false)}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={loading}
                className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-40 transition-colors"
              >
                {loading ? 'Importing…' : `Yes — create ${preview.totals.orders} order${preview.totals.orders !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
          {step === 'preview' && !confirmImport && (
            <>
              <button
                onClick={() => { setStep('select'); setPreview(null); setError(null); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleDownload}
                disabled={loading || !preview.ok}
                title={!preview.ok ? 'Fix all issues first' : undefined}
                className="px-4 py-2 border border-gray-300 bg-white text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Download Import CSV
              </button>
              <button
                onClick={() => setConfirmImport(true)}
                disabled={loading || !preview.ok}
                title={!preview.ok ? 'Fix all issues first' : undefined}
                className="px-5 py-2 bg-sky-700 text-white text-sm font-medium rounded-lg hover:bg-sky-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Send to Import Tool…
              </button>
            </>
          )}
          {step === 'imported' && (
            <button
              onClick={onClose}
              className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
